import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../shared/components';
import { LayoutShell } from '../../shared/layouts';
import { feedback, sconAppService } from '../../shared/services';

export const SconAppWhatsApp: React.FC = () => {
  const [qrCode, setQrCode] = useState<string | null>(null);

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['sconapp-whatsapp-status'],
    queryFn: () => sconAppService.getStatus(),
    refetchInterval: (data) => (data?.connected ? false : 5000),
  });

  const { mutate: connect, isLoading: isConnecting } = useMutation(
    () => sconAppService.connect(),
    {
      onSuccess: (data) => {
        if (data.qrCodeBase64) {
          setQrCode(data.qrCodeBase64);
        } else {
          feedback('QR Code gerado, mas sem imagem para exibir', 'warning');
        }
      },
      onError: (err: Error) => feedback(err.message, 'error'),
    }
  );

  const { mutate: disconnect, isLoading: isDisconnecting } = useMutation(
    () => sconAppService.disconnect(),
    {
      onSuccess: () => {
        setQrCode(null);
        refetchStatus();
        feedback('WhatsApp SconApp desconectado', 'success');
      },
      onError: (err: Error) => feedback(err.message, 'error'),
    }
  );

  useEffect(() => {
    if (status?.connected) {
      setQrCode(null);
    }
  }, [status?.connected]);

  return (
    <LayoutShell>
      <Box display='flex' flexDirection='column' gap={3}>
        <Typography variant='h4'>WhatsApp SconApp</Typography>
        <Typography variant='body1' color='text.secondary'>
          Conecte o número <strong>{status?.phoneNumber || '+5551993848249'}</strong> para enviar
          códigos de confirmação de telefone e outras mensagens automáticas do SconApp.
        </Typography>

        <Card>
          <CardContent>
            <Box display='flex' flexDirection='column' gap={2} alignItems='center'>
              <Typography variant='h6'>
                Status: {status?.connected ? '✅ Conectado' : '❌ Desconectado'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Instância: {status?.instanceName || 'sconapp'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Número: {status?.phoneNumber || '+5551993848249'}
              </Typography>

              <Divider sx={{ width: '100%' }} />

              {qrCode && (
                <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Escaneie o QR Code com o WhatsApp do número {status?.phoneNumber || '+5551993848249'}:
                  </Typography>
                  <Box
                    component='img'
                    src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                    alt='QR Code'
                    sx={{
                      width: 256,
                      height: 256,
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 1,
                    }}
                  />
                </Box>
              )}

              <Box display='flex' gap={2}>
                {!status?.connected ? (
                  <Button
                    label={qrCode ? 'Gerar Novo QR Code' : 'Conectar WhatsApp'}
                    variant='contained'
                    onClick={() => connect()}
                    isLoading={isConnecting}
                  />
                ) : (
                  <Button
                    label='Desconectar'
                    variant='outlined'
                    color='error'
                    onClick={() => disconnect()}
                    isLoading={isDisconnecting}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </LayoutShell>
  );
};
